import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { trashOutline, refreshOutline, checkmarkCircle, alertCircle, printOutline, star, starOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useMasters } from '../../lib/data/queries.js';
import { usePageActivity } from '../../lib/hooks/usePageActivity.js';
import {
  useDevicePrinters,
  addDevicePrinter,
  removeDevicePrinter,
  setDefaultDevicePrinter,
  importLegacyPrinters,
  type DevicePrinter,
} from '../../lib/printers/devicePrinters.js';
import { bleScanPrinters, isNiimbotDevice, type BlePrinterDevice } from '../../lib/adapters/printers/ble-transport.js';
import { isY428Printer, tsplProfile } from '../../lib/adapters/printers/printerProfile.js';
import { printerTestTemplate } from '../../lib/adapters/printers/printerTestTemplate.js';
import { OFFSET_X_MM, OFFSET_Y_MM, SHRINK_X_MM, SHRINK_Y_MM } from '../../lib/adapters/printers/labelRaster.js';
import { SYSTEM_PRINTER_NAME, SYSTEM_PRINTER_ADDRESS, qzListPrinters, PREVIEW_PRINTER_ID, PREVIEW_PRINTER_NAME } from '../../lib/adapters/printers/index.js';
import {
  classicListPaired, classicConnect, classicWriteBytes, classicIsConnected,
  classicForceDisconnect,
  type ClassicPrinterDevice,
} from '../../lib/adapters/printers/bt-classic-transport.js';

const isAndroid = Capacitor.getPlatform() === 'android';
// T-272: la "Stampante di sistema" (stampa via driver Windows, es. Meteor Mirò
// USB) ha senso solo nella PWA web (desktop Chrome). Su app native si usano
// i transport BLE/BT Classic.
const isWeb = !Capacitor.isNativePlatform();

// T-281/T-273/T-272: 'system' (window.print) e 'qz' (QZ Tray) sono SOLO web/PWA
// (niente stampanti USB su smartphone). Vanno escluse da import/lista su nativo.
function isPrinterUsableHere(driver: string): boolean {
  if ((driver === 'system' || driver === 'qz') && !isWeb) return false;
  return true;
}

interface PrinterMaster {
  id: string;
  name: string;
  driver: string;
  address: string;
  business_id: string;
  // T-081 follow-up: 'bluetooth' = BT Classic SPP (Clabel CT221B, Xprinter,
  // HPRT, ecc.) · 'ble' = Bluetooth Low Energy (Niimbot, Phomemo, BLE-only
  // Brother). Salvato dal pair flow per scegliere il transport giusto
  // su test/calibrazione/stampa. Stampanti pre-T-081 hanno connection
  // undefined → fallback 'bluetooth' (comportamento legacy).
  connection?: 'bluetooth' | 'ble' | string;
  version?: number;
  active?: boolean;
  // T-281: per importare i master legacy nel device store servono anche queste.
  label_size_mm?: { width: number; height: number };
  dpi?: number;
}

export function PrinterConfigPage(): JSX.Element {
  const { profile } = useAuth();
  const { isActive, capture } = usePageActivity('/settings/printer');
  // T-281: legacyMasters serve SOLO per importare le stampanti vecchie
  // (tenant-scoped) nel device store; la lista vera è device-local.
  const { data: legacyMasters = [] } = useMasters<PrinterMaster>('printers');
  const { printers: devicePrinters, defaultId, reload: reloadDevicePrinters } = useDevicePrinters();
  const [toast, setToast] = useState<string | null>(null);

  const [scanning, setScanning] = useState(false);
  const [foundBle, setFoundBle] = useState<BlePrinterDevice[]>([]);
  const [foundClassic, setFoundClassic] = useState<ClassicPrinterDevice[]>([]);
  const [connStatus, setConnStatus] = useState<Record<string, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<DevicePrinter | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  const [paperMm, setPaperMm] = useState<number>(50);

  // T-273: stampanti di sistema via QZ Tray (helper locale, web-only).
  const [qzScanning, setQzScanning] = useState(false);
  const [foundQz, setFoundQz] = useState<string[]>([]);

  // ───── Refresh stato live ogni 5 s per le stampanti BT Classic ─────
  useEffect(() => {
    if (!isAndroid || !isActive) return;
    const isCurrentVisit = capture();
    const update = async () => {
      const out: Record<string, boolean> = {};
      for (const p of devicePrinters) {
        if (p.driver === 'preview') continue;
        out[p.address] = await classicIsConnected(p.address);
      }
      if (isCurrentVisit()) setConnStatus(out);
    };
    void update();
    const t = setInterval(() => { void update(); }, 5_000);
    return () => clearInterval(t);
  }, [capture, devicePrinters, isActive]);

  // ───── T-281: import-on-sight delle stampanti legacy (master tenant-scoped)
  // nel device store, così le configurazioni esistenti non si perdono e
  // diventano disponibili in TUTTI i locali. Esclude system/qz su nativo. ─────
  useEffect(() => {
    if (legacyMasters.length === 0) return;
    const legacy: DevicePrinter[] = legacyMasters
      .filter((m) => m.driver !== 'preview' && isPrinterUsableHere(m.driver))
      .map((m) => ({
        id: m.id,
        name: m.name,
        driver: m.driver as DevicePrinter['driver'],
        connection: (m.connection ?? 'bluetooth') as DevicePrinter['connection'],
        address: m.address,
        label_size_mm: m.label_size_mm ?? { width: 50, height: 30 },
        dpi: m.dpi ?? 203,
      }));
    void importLegacyPrinters(legacy).then((n) => { if (n > 0) reloadDevicePrinters(); });
  }, [legacyMasters, reloadDevicePrinters]);

  // ───── Scan iniziale paired Android ─────
  async function loadPaired(): Promise<void> {
    if (!isAndroid) return;
    try {
      const list = await classicListPaired();
      // 2026-05-24: Niimbot va via SPP Classic (APK conferma), quindi DEVE
      // apparire nei paired Classic. Manteniamo solo il dedup per nome
      // (eventuali duplicati MAC dovuti a doppio bonding).
      const seen = new Set<string>();
      const filtered = list.filter((d) => {
        const n = (d.name || d.address).toLowerCase();
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      });
      setFoundClassic(filtered);
    } catch (err) {
      setToast(`Errore lettura paired: ${toUserMessage(err)}`);
    }
  }
  useEffect(() => { void loadPaired(); }, []);

  async function startScan(): Promise<void> {
    // T-219: su iOS (Safari e qualsiasi browser iOS) Web Bluetooth non esiste:
    // senza questo guard lo scan moriva con un errore generico incomprensibile.
    if (!Capacitor.isNativePlatform() && !('bluetooth' in navigator)) {
      setToast(
        'Questo browser non supporta il Bluetooth (Web Bluetooth). Su iPhone/iPad usa ' +
        "l'app TrackEAT installata; dal computer o da Android usa Chrome o Edge.",
      );
      return;
    }
    setScanning(true);
    setFoundBle([]);
    setFoundClassic([]);
    try {
      if (isAndroid) {
        // T-081 follow-up: scan COMPLETO su Android = Classic discovery (~5s
        // per stampanti BT2.0/SPP come Clabel CT221B) IN PARALLELO con BLE
        // scan (~8s per stampanti BLE come Niimbot, Phomemo, alcune Brother).
        // Prima il flow chiamava solo classicListPaired (= solo bonded), per
        // cui le stampanti non ancora accoppiate erano invisibili → l'utente
        // doveva andare in Impostazioni Android e fare il pairing manuale.
        // Ora le scopriamo tutte direttamente dall'app.
        setToast('Ricerca stampanti in corso (BT + BLE, ~8s)…');
        const [classicResult, bleResult] = await Promise.all([
          classicListPaired().catch((err) => {
            // non bloccare l'altro scan se Classic fallisce.
            void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
              dbg.warn('printer-scan', 'classic_scan_failed', {
                error: (err instanceof Error) ? err.message : String(err),
              }),
            );
            return [] as ClassicPrinterDevice[];
          }),
          bleScanPrinters(8_000, { allServices: true }).catch((err) => {
            void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
              dbg.warn('printer-scan', 'ble_scan_failed', {
                error: (err instanceof Error) ? err.message : String(err),
              }),
            );
            return [] as BlePrinterDevice[];
          }),
        ]);
        // 2026-05-24: Niimbot va via SPP Classic. Mostriamo le Niimbot
        // sia in foundClassic (se paired) sia in foundBle (advertising).
        // Filtriamo solo i duplicati per nome (lo stesso B1 può apparire
        // 2 volte in BLE con MAC ruotati = advertising legacy + ext BLE5).
        setFoundClassic(classicResult);
        const classicAddrs = new Set(classicResult.map((d) => d.address.toLowerCase()));
        const classicNames = new Set(classicResult.map((d) => (d.name || '').toLowerCase()));
        const seenBleNames = new Set<string>();
        const bleFiltered = bleResult.filter((d) => {
          if (classicAddrs.has(d.deviceId.toLowerCase())) return false;
          const n = (d.name || '').toLowerCase();
          if (!n) return true; // niente nome → tieni (sortita per RSSI)
          if (classicNames.has(n)) return false; // stesso nome → in Classic
          if (seenBleNames.has(n)) return false; // duplicato BLE legacy+ext
          seenBleNames.add(n);
          return true;
        });
        setFoundBle(bleFiltered);
        const totalFound = classicResult.length + bleFiltered.length;
        if (totalFound === 0) {
          setToast(
            'Nessuna stampante trovata. Verifica che sia accesa, in modalità pairing ' +
            '(spia lampeggiante o pulsante FEED tenuto premuto), e che siano attivi ' +
            'Bluetooth + Posizione su Android. Riprova.',
          );
        } else {
          setToast(`Trovati ${totalFound} dispositivi. Tocca quello giusto per accoppiarlo.`);
        }
      } else {
        const devices = await bleScanPrinters(8_000, { allServices: true });
        setFoundBle(devices);
        if (devices.length === 0) {
          setToast('Nessuna stampante trovata. Assicurati che la stampante sia accesa e in modalità pairing.');
        } else {
          setToast(`Trovati ${devices.length} dispositivi. Tocca quello giusto per accoppiarlo.`);
        }
      }
    } catch (err) {
      setToast(`Errore scan: ${toUserMessage(err)}`);
    } finally {
      setScanning(false);
    }
  }

  async function deletePrinter(p: DevicePrinter): Promise<void> {
    try {
      await removeDevicePrinter(p.id);
      reloadDevicePrinters();
      setToast(`Stampante "${p.name}" rimossa`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setDeleteTarget(null);
    }
  }

  async function makeDefault(p: DevicePrinter): Promise<void> {
    try {
      await setDefaultDevicePrinter(p.id);
      reloadDevicePrinters();
      setToast(`"${p.name}" è ora la stampante predefinita`);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // T-424: rende l'Anteprima a schermo la stampante predefinita (demo/PWA senza
  // hardware). Il default store accetta anche l'id virtuale dell'anteprima e
  // resolveDefaultPrinterId lo gestisce come fallback ovunque.
  async function makePreviewDefault(): Promise<void> {
    try {
      await setDefaultDevicePrinter(PREVIEW_PRINTER_ID);
      reloadDevicePrinters();
      setToast('Anteprima a schermo impostata come predefinita (demo)');
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function savePrinter(name: string, address: string, connKind: 'classic' | 'ble' = 'classic'): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast('Nessun locale attivo'); return;
    }
    const duplicate = devicePrinters.find((p) => p.address === address);
    if (duplicate) {
      setToast(`"${duplicate.name}" è già configurata`);
      return;
    }
    // T-082: Niimbot B1 driver — usa BLE (deviceType=2 confermato).
    // L'APK ha B21SppFactory (B21=SPP) e B1Factory (B1=BLE) → modelli diversi.
    // L'app ufficiale comunica via L2CAP CoC, non GATT, e attualmente
    // TrackEAT supporta solo GATT → la stampa bitmap non funziona ancora
    // (per ora blocchiamo il salvataggio con messaggio chiaro).
    const isNiimbot = isNiimbotDevice(name);
    const driverName = isNiimbot ? 'niimbot' : 'tspl';
    if (isNiimbot) {
      setToast(
        '⚠️ La Niimbot B1 richiede L2CAP CoC (variante BLE avanzata) per ' +
        'stampare. Stiamo sviluppando il supporto. Per ora: usa l\'app ' +
        'Niimbot ufficiale oppure passa alla Clabel CT221B.',
      );
      return;
    }
    if (isY428Printer(name) && (!isAndroid || connKind !== 'classic')) {
      setToast('Y428BT: seleziona la stampante nella ricerca Bluetooth Classic dell’app Android.');
      return;
    }
    const id = crypto.randomUUID();
    const labelSize = paperMm === 50
      ? { width: 50, height: 30 }
      : paperMm === 60
        ? { width: 60, height: 40 }
        : paperMm <= 80
          ? { width: 80, height: 60 }
          : { width: 100, height: 70 };
    try {
      // T-081 follow-up: usare il transport CORRETTO in base al tipo di
      // device. Prima si chiamava SEMPRE `classicConnect` (= BT Classic
      // SPP) anche per device BLE → il connect falliva con errore generico
      // mascherato dal catch, l'utente vedeva "non si collega".
      // Ora: classic → BluetoothSerial.connect (triggera bonding/PIN se serve);
      //      ble → BleClient.connect (no bonding, scan-and-go).
      // Su iOS / PWA: solo BLE, niente Classic SPP disponibile.
      if (connKind === 'classic') {
        if (isAndroid) {
          try {
            await classicConnect(address);
          } catch (e) {
            // Errore tipico per Niimbot: utente l'ha pairata via BLE, non Classic.
            // Mostriamo istruzioni chiare invece di "errore generico riprova".
            if (isNiimbot) {
              setToast(
                '⚠️ La Niimbot va accoppiata da Impostazioni → Bluetooth ' +
                '(non da TrackEAT). Apri Impostazioni Bluetooth Android, ' +
                'cerca "' + name + '", clicca "Accoppia" (PIN 0000 se richiesto), ' +
                'poi torna qui e riprova.',
              );
              return;
            }
            throw e;
          }
        }
      } else {
        // BLE: import dinamico per non caricare il modulo su web/PWA.
        const { bleConnect } = await import('../../lib/adapters/printers/ble-transport.js');
        await bleConnect(address);
      }
      // T-281: device-local (Capacitor Preferences), non più master tenant-scoped.
      // La stampante resta disponibile in TUTTI i locali su questo dispositivo.
      await addDevicePrinter({
        id,
        name,
        driver: driverName,
        // T-081 follow-up: persistiamo il connection type corretto così
        // futuri test/stampe usano il transport giusto senza ri-rilevamento.
        connection: connKind === 'ble' ? 'ble' : 'bluetooth',
        address,
        label_size_mm: labelSize,
        dpi: tsplProfile(name).dpi,
      });
      reloadDevicePrinters();
      setToast(`"${name}" salvata e connessa.`);
      setFoundBle([]);
      setFoundClassic([]);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // T-272: aggiunge la "Stampante di sistema" (web/PWA). Non c'è pairing: la
  // stampa passa dal driver Windows della stampante installata (es. Meteor
  // Mirò USB) via window.print(). La stampante fisica reale la sceglie
  // Windows/Chrome al momento della stampa (o la predefinita in kiosk-printing).
  async function addSystemPrinter(): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast('Nessun locale attivo'); return;
    }
    const existing = devicePrinters.find((p) => p.driver === 'system');
    if (existing) {
      setToast(`"${existing.name}" è già configurata`);
      return;
    }
    const id = crypto.randomUUID();
    const labelSize = paperMm === 50
      ? { width: 50, height: 30 }
      : paperMm === 60
        ? { width: 60, height: 40 }
        : paperMm <= 80
          ? { width: 80, height: 60 }
          : { width: 100, height: 70 };
    try {
      await addDevicePrinter({
        id,
        name: SYSTEM_PRINTER_NAME,
        driver: 'system',
        connection: 'usb',
        address: SYSTEM_PRINTER_ADDRESS,
        label_size_mm: labelSize,
        dpi: 203,
      });
      reloadDevicePrinters();
      setToast('Stampante di sistema aggiunta. Selezionala nella pagina Etichette.');
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // T-273: cerca le stampanti di sistema tramite QZ Tray (helper locale).
  async function scanQz(): Promise<void> {
    setQzScanning(true);
    setFoundQz([]);
    try {
      const list = await qzListPrinters();
      setFoundQz(list);
      if (list.length === 0) {
        setToast('QZ Tray connesso ma nessuna stampante trovata. Verifica che la stampante sia installata in Windows.');
      } else {
        setToast(`Trovate ${list.length} stampanti. Tocca la Meteor Mirò per selezionarla.`);
      }
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setQzScanning(false);
    }
  }

  // T-273: salva una stampante di sistema scelta via QZ Tray (driver 'qz',
  // address = nome esatto della stampante; la predefinita di Windows resta
  // libera per Word/Excel).
  async function saveQzPrinter(printerName: string): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast('Nessun locale attivo'); return;
    }
    const existing = devicePrinters.find((p) => p.driver === 'qz' && p.address === printerName);
    if (existing) {
      setToast(`"${existing.name}" è già configurata`);
      return;
    }
    const id = crypto.randomUUID();
    const labelSize = paperMm === 50
      ? { width: 50, height: 30 }
      : paperMm === 60
        ? { width: 60, height: 40 }
        : paperMm <= 80
          ? { width: 80, height: 60 }
          : { width: 100, height: 70 };
    try {
      await addDevicePrinter({
        id,
        name: printerName,
        driver: 'qz',
        connection: 'usb',
        address: printerName,
        label_size_mm: labelSize,
        dpi: 203,
      });
      reloadDevicePrinters();
      setToast(`"${printerName}" salvata. Selezionala nella pagina Etichette.`);
      setFoundQz([]);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // T-081 follow-up: helper centrale che sceglie il transport in base al
  // campo `connection` della stampante salvata. Prima ogni metodo (testPrint,
  // calibrationPrint, bitmapTest, realPrint) faceva `if (isAndroid) classic
  // else ble`, sbagliato per stampanti BLE su Android (Niimbot, Phomemo). Ora
  // legge p.connection: 'ble' → BleClient, 'bluetooth' o legacy → SPP.
  async function connectAndWriteForPrinter(p: DevicePrinter, bytes: Uint8Array): Promise<void> {
    const isBle = p.connection === 'ble';
    if (isBle) {
      const { bleConnect, bleWriteBytes } = await import('../../lib/adapters/printers/ble-transport.js');
      await bleConnect(p.address);
      await bleWriteBytes(p.address, bytes);
      return;
    }
    // Default = 'bluetooth' = BT Classic SPP. Su iOS/PWA non disponibile.
    if (!isAndroid) {
      const { bleConnect, bleWriteBytes } = await import('../../lib/adapters/printers/ble-transport.js');
      await bleConnect(p.address);
      await bleWriteBytes(p.address, bytes);
      return;
    }
    await classicConnect(p.address);
    await classicWriteBytes(p.address, bytes);
  }

  async function y428Test(p: DevicePrinter): Promise<void> {
    const { createPrinterAdapter } = await import('../../lib/adapters/printers/index.js');
    const adapter = createPrinterAdapter('tspl');
    const session = await adapter.connect({ ...p, dpi: 300, driver: 'tspl' });
    await adapter.printLabel(session, printerTestTemplate(), {}, 1);
    setToast('Prova Y428BT inviata a 300 dpi. Verifica il quadrato 10 × 10 mm, gli accenti e il QR: TRACKEAT-Y428BT-TEST.');
  }

  async function testPrint(p: DevicePrinter): Promise<void> {
    setTesting(p.id);
    try {
      if (p.driver === 'tspl' && isY428Printer(p.name)) {
        await y428Test(p);
        return;
      }
      // T-272/T-273: stampanti "di sistema" (web) → stampa via adapter dedicato
      // ('system' = window.print su iframe · 'qz' = QZ Tray per nome), non BT.
      if (p.driver === 'system' || p.driver === 'qz') {
        const { createPrinterAdapter } = await import('../../lib/adapters/printers/index.js');
        const driver = p.driver as 'system' | 'qz';
        const adapter = createPrinterAdapter(driver);
        const template: import('@trackeat/shared').LabelTemplate = {
          id: 'test-system', tenant_id: '', business_id: null, name: 'TEST',
          width_mm: 50, height_mm: 30, preset: 'lavorato',
          required_allergens: false, version: 0,
          created_at: new Date().toISOString(), archived_at: null,
          elements: [
            { id: 'e1', type: 'text', x_mm: 4, y_mm: 4, rotation: 0, font_size_pt: 12, font_weight: 'bold', static_value: 'TRACKEAT TEST' },
            { id: 'e2', type: 'text', x_mm: 4, y_mm: 12, rotation: 0, font_size_pt: 8, static_value: new Date().toLocaleString('it-IT') },
            { id: 'e3', type: 'text', x_mm: 4, y_mm: 20, rotation: 0, font_size_pt: 7, static_value: p.name },
          ],
        };
        const session = await adapter.connect({
          id: p.address, name: p.name, driver,
          connection: 'usb', address: p.address, model_hint: null,
        });
        await adapter.printLabel(session, template, {}, 1);
        setToast(driver === 'qz'
          ? 'Stampa di prova inviata via QZ Tray. Se non esce, verifica che QZ Tray sia in esecuzione e di aver dato "Allow".'
          : 'Stampa di prova inviata al sistema. Se non esce nulla, scegli la stampante nel dialogo di Chrome (o impostala come predefinita).');
        return;
      }
      // TSPL minimo: etichetta 50×30 con testo "TEST TRACKEAT" + data/ora.
      // T-082: per Niimbot serve il driver dedicato (protocollo binario),
      // non TSPL. Usiamo il factory createPrinterAdapter che torna il
      // driver corretto in base a p.driver.
      if (p.driver === 'niimbot') {
        const { createPrinterAdapter } = await import('../../lib/adapters/printers/index.js');
        const { clearNiimbotState } = await import('../../lib/adapters/printers/niimbot.js');
        // 2026-05-24 SWITCH: ora Niimbot via SPP. Forza disconnect SPP prima
        // del nuovo test per evitare sessioni stale (firmware in sleep).
        try { await classicForceDisconnect(p.address); } catch { /* ignore */ }
        clearNiimbotState(p.address);
        await new Promise((r) => setTimeout(r, 500));
        const adapter = createPrinterAdapter('niimbot');
        const template: import('@trackeat/shared').LabelTemplate = {
          id: 'test-niimbot', tenant_id: '', business_id: null, name: 'TEST',
          width_mm: 50, height_mm: 30, preset: 'lavorato',
          required_allergens: false, version: 0,
          created_at: new Date().toISOString(), archived_at: null,
          elements: [
            { id: 'e1', type: 'text', x_mm: 4, y_mm: 4, rotation: 0, font_size_pt: 12, font_weight: 'bold', static_value: 'TRACKEAT TEST' },
            { id: 'e2', type: 'text', x_mm: 4, y_mm: 12, rotation: 0, font_size_pt: 8, static_value: new Date().toLocaleString('it-IT') },
            { id: 'e3', type: 'text', x_mm: 4, y_mm: 20, rotation: 0, font_size_pt: 7, static_value: p.name },
          ],
        };
        const session = await adapter.connect({
          id: p.address, name: p.name, driver: 'niimbot',
          connection: 'bluetooth', address: p.address, model_hint: null,
        });
        const { getNiimbotTrace } = await import('../../lib/adapters/printers/niimbot.js');
        let printErr: unknown = null;
        try {
          await adapter.printLabel(session, template, {}, 1);
        } catch (e) {
          printErr = e;
        }
        const t = getNiimbotTrace();
        const tag = printErr ? `❌ ERR: ${(printErr as Error).message}` : '✅ Stampa inviata (SPP)';
        setToast(`${tag}\n${t.lines.join('\n')}`);
        return;
      }
      const tspl =
        'SIZE 50 mm,30 mm\r\n' +
        'GAP 2 mm,0\r\n' +
        'CLS\r\n' +
        'TEXT 30,30,"3",0,1,1,"TRACKEAT TEST"\r\n' +
        `TEXT 30,80,"2",0,1,1,"${new Date().toLocaleString('it-IT')}"\r\n` +
        `TEXT 30,130,"1",0,1,1,"${p.name}"\r\n` +
        'PRINT 1,1\r\n';
      const bytes = new TextEncoder().encode(tspl);
      await connectAndWriteForPrinter(p, bytes);
      setToast(`Stampa di prova inviata a "${p.name}".`);
    } catch (err) {
      setToast(`Errore stampa di prova: ${toUserMessage(err)}`);
    } finally {
      setTesting(null);
    }
  }

  async function calibrationPrint(p: DevicePrinter): Promise<void> {
    setTesting(p.id);
    try {
      if (p.driver === 'tspl' && isY428Printer(p.name)) {
        await y428Test(p);
        return;
      }
      // Pattern di calibrazione su area utile 49×29 mm (rotolo fisico
      // 50×30 mm). 1 mm di shift dx/giù applicato come OFFSET additivo a
      // OGNI coordinata: il firmware Clabel ignora il REFERENCE TSPL.
      // 1 mm = 8 dot a 203 DPI.
      const OFF_X = 8; // 1 mm shift dx
      const OFF_Y = 4; // 0.5 mm shift giù
      const W_DOT = 50 * 8; // 400
      const H_DOT = 29 * 8; // 232
      const lines: string[] = [
        'SIZE 50 mm,30 mm',
        'GAP 2 mm,0',
        'DIRECTION 0',
        'CLS',
        `BOX ${OFF_X},${OFF_Y},${OFF_X + W_DOT - 1},${OFF_Y + H_DOT - 1},2`,
        // L marker in alto a sx (origine logica 0,0 → fisica OFF_X,OFF_Y).
        `BAR ${OFF_X},${OFF_Y},40,2`,
        `BAR ${OFF_X},${OFF_Y},2,40`,
      ];
      for (let mm = 5; mm <= 45; mm += 5) {
        const x = mm * 8 + OFF_X;
        lines.push(`BAR ${x},${OFF_Y},1,16`);
        lines.push(`BAR ${x},${OFF_Y + H_DOT - 16},1,16`);
        const labelW = mm >= 10 ? 16 : 8;
        const tx = Math.max(OFF_X, x - labelW - 2);
        lines.push(`TEXT ${tx},${OFF_Y + 4},"1",0,1,1,"${mm}"`);
      }
      for (let mm = 5; mm <= 25; mm += 5) {
        const y = mm * 8 + OFF_Y;
        lines.push(`BAR ${OFF_X},${y},16,1`);
        lines.push(`BAR ${OFF_X + W_DOT - 16},${y},16,1`);
        const ty = Math.max(OFF_Y, y - 14);
        lines.push(`TEXT ${OFF_X + 18},${ty},"1",0,1,1,"${mm}"`);
      }
      lines.push('PRINT 1,1');
      const tspl = lines.join('\r\n') + '\r\n';
      const bytes = new TextEncoder().encode(tspl);
      await connectAndWriteForPrinter(p, bytes);
      setToast(`Calibrazione inviata a "${p.name}". Misura quanto manca dai bordi.`);
    } catch (err) {
      setToast(`Errore calibrazione: ${toUserMessage(err)}`);
    } finally {
      setTesting(null);
    }
  }

  // TEST BITMAP — verifica che la Clabel CT221B supporti il comando `BITMAP`
  // (stampa raster a immagine). Se questo esce stampato bene, possiamo
  // abbandonare i font ROM fissi e renderizzare l'etichetta come immagine
  // (font libero, accenti, WYSIWYG reale). 50×30 mm @203 DPI.
  async function bitmapTest(p: DevicePrinter): Promise<void> {
    setTesting(p.id);
    try {
      if (p.driver === 'tspl' && isY428Printer(p.name)) {
        await y428Test(p);
        return;
      }
      const DPI = 203;
      // W_PX MULTIPLO DI 8 forzato: così bytes/riga è esatto (niente padding
      // ambiguo). 50 mm @203 dpi ≈ 400 dot = 50 byte/riga tondi.
      const W_PX = 400;          // 50 byte/riga esatti
      const H_PX = 240;          // ~30 mm
      const cv = document.createElement('canvas');
      cv.width = W_PX;
      cv.height = H_PX;
      const ctx = cv.getContext('2d');
      if (!ctx) throw new Error('Canvas non disponibile');
      // === SCHEDA DI CALIBRAZIONE CARATTERI (50×30 mm) ===
      // STESSE formule della stampa reale (labelRaster.ts): misurare questa
      // scheda col righello tara direttamente la produzione.
      //   mmToDots = round(mm / 25.4 * 203)   (1 mm ≈ 7.99 dot)
      //   ptToPx   = max(6, round(pt * 203 / 72))
      const D = (mm: number): number => Math.round((mm / 25.4) * DPI);
      const PT = (pt: number): number => Math.max(6, Math.round((pt * DPI) / 72));
      const FONT = '"Helvetica Neue","Helvetica","Arial","Liberation Sans",sans-serif';
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, W_PX, H_PX);
      ctx.fillStyle = '#000';
      // Stesso offset + restringimento della stampa reale (labelRaster).
      ctx.translate(D(OFFSET_X_MM), D(OFFSET_Y_MM));
      ctx.scale((W_PX - D(SHRINK_X_MM)) / W_PX, (H_PX - D(SHRINK_Y_MM)) / H_PX);
      // Cornice 1 dot (riferimento bordo).
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, W_PX - 1, H_PX - 1);

      // --- RIGHELLO ORIZZONTALE (in alto): tacche ogni 1 mm, 0..50 ---
      for (let mm = 0; mm <= 50; mm += 1) {
        const x = D(mm);
        const len = mm % 10 === 0 ? 16 : mm % 5 === 0 ? 10 : 5;
        ctx.fillRect(x, 1, 1, len);
        if (mm % 10 === 0) {
          ctx.font = `400 ${PT(6)}px ${FONT}`;
          ctx.textBaseline = 'top';
          ctx.fillText(`${mm}`, Math.min(x + 2, W_PX - 14), 18);
        }
      }
      // --- RIGHELLO VERTICALE (a sinistra): tacche ogni 1 mm, 0..30 ---
      for (let mm = 0; mm <= 30; mm += 1) {
        const y = D(mm);
        const len = mm % 10 === 0 ? 16 : mm % 5 === 0 ? 10 : 5;
        ctx.fillRect(1, y, len, 1);
        if (mm % 10 === 0 && mm > 0) {
          ctx.font = `400 ${PT(6)}px ${FONT}`;
          ctx.textBaseline = 'top';
          ctx.fillText(`${mm}`, 18, Math.min(y + 1, H_PX - 12));
        }
      }

      // --- QUADRATO GEOMETRICO 10×10 mm (verifica scala 1:1) ---
      const boxX = D(36);
      const boxY = D(3);
      ctx.lineWidth = 1;
      ctx.strokeRect(boxX + 0.5, boxY + 0.5, D(10), D(10));
      ctx.font = `700 ${PT(6)}px ${FONT}`;
      ctx.textBaseline = 'top';
      ctx.fillText('10x10mm', boxX - 2, boxY + D(10) + 2);

      // --- CAMPIONI DI TESTO a corpi noti ---
      // Per ogni riga stampo: "<pt>pt h≈<atteso>mm" + testo campione, e una
      // STANGHETTA verticale alta esattamente il valore atteso (em-box) a
      // sinistra del testo. Se le lettere non combaciano con la stanghetta,
      // il rendering NON è 1:1 e il rapporto misurato è il fattore di
      // correzione da applicare a ptToPx.
      const samples = [6, 8, 10, 12];
      let yCur = D(4);
      const colLabelX = D(3);
      const colBarX = D(20);
      const colTextX = D(22);
      for (const pt of samples) {
        const px = PT(pt);
        const emMm = px / (DPI / 25.4); // altezza em attesa in mm
        const lineH = Math.round(px * 1.25);
        // Etichetta
        ctx.font = `400 ${PT(6)}px ${FONT}`;
        ctx.textBaseline = 'top';
        ctx.fillText(`${pt}pt ~${emMm.toFixed(1)}mm`, colLabelX, yCur + Math.max(0, (px - PT(6)) / 2));
        // Stanghetta = altezza em attesa (px). Misurala: deve dare emMm.
        ctx.fillRect(colBarX, yCur, 2, px);
        // Testo campione (ascendenti+discendenti+accenti+cifre).
        ctx.font = `400 ${px}px ${FONT}`;
        ctx.textBaseline = 'top';
        ctx.fillText('Hg0123 àè|', colTextX, yCur);
        yCur += lineH + 2;
      }
      ctx.font = `400 ${PT(5)}px ${FONT}`;
      ctx.textBaseline = 'top';
      ctx.fillText('Misura col righello e dimmi i mm reali', colLabelX, H_PX - D(2.4));

      // Soglia 1-bit + packing TSPL BITMAP (MSB-first, riga padded a byte).
      // Convenzione TSPL: bit 0 = punto nero stampato, bit 1 = bianco.
      const INVERT = false; // se esce in negativo, metti true e rideploy.
      const img = ctx.getImageData(0, 0, W_PX, H_PX).data;
      const rowBytes = Math.ceil(W_PX / 8);
      const raster = new Uint8Array(rowBytes * H_PX);
      raster.fill(0xff); // tutto bianco (bit 1)
      for (let py = 0; py < H_PX; py += 1) {
        for (let px = 0; px < W_PX; px += 1) {
          const i = (py * W_PX + px) * 4;
          const lum = 0.299 * img[i]! + 0.587 * img[i + 1]! + 0.114 * img[i + 2]!;
          let black = lum < 128;
          if (INVERT) black = !black;
          if (black) {
            const byteIdx = py * rowBytes + (px >> 3);
            raster[byteIdx]! &= ~(0x80 >> (px & 7)); // azzera il bit → nero
          }
        }
      }

      const header =
        'SIZE 50 mm,30 mm\r\n' +
        'GAP 2 mm,0\r\n' +
        'DIRECTION 0\r\n' +
        'CLS\r\n' +
        `BITMAP 0,0,${rowBytes},${H_PX},0,`;
      const footer = '\r\nPRINT 1,1\r\n';
      const head = new TextEncoder().encode(header);
      const foot = new TextEncoder().encode(footer);
      const job = new Uint8Array(head.length + raster.length + foot.length);
      job.set(head, 0);
      job.set(raster, head.length);
      job.set(foot, head.length + raster.length);

      // La "test BMP" deve usare lo STESSO transport binary-safe delle etichette
      // reali e della stampa "Test". Su stampanti USB da PC (driver qz/system) il
      // raster va mandato come IMMAGINE (PNG → QZ Tray / window.print, che il
      // driver Windows rasterizza), NON come byte TSPL grezzi: il path raw-TSPL
      // (connectAndWriteForPrinter) parla solo BLE/SPP, e su USB produceva
      // l'etichetta illeggibile "a righe" (segnalazione LeBoccerie 2026-06-26).
      if (p.driver === 'qz') {
        const { qzPrintLabel } = await import('../../lib/adapters/printers/qz-transport.js');
        await qzPrintLabel({
          printerName: p.address, pngDataUrl: cv.toDataURL('image/png'),
          widthMm: 50, heightMm: 30, dpi: DPI, copies: 1,
        });
      } else if (p.driver === 'system') {
        const { buildSystemPrintDocument } = await import('../../lib/adapters/printers/system-print-doc.js');
        const { printHtmlViaIframe } = await import('../../lib/adapters/printers/system-print.js');
        await printHtmlViaIframe(buildSystemPrintDocument({
          pngDataUrl: cv.toDataURL('image/png'), widthMm: 50, heightMm: 30, copies: 1, title: 'TEST BITMAP',
        }));
      } else {
        await connectAndWriteForPrinter(p, job);
      }
      setToast(`Scheda calibrazione inviata a "${p.name}". Misura col righello: 50mm, riquadro 10x10, e l'altezza reale di ogni riga di testo.`);
    } catch (err) {
      setToast(`Errore test BITMAP: ${toUserMessage(err)}`);
    } finally {
      setTesting(null);
    }
  }

  // La stampante "Anteprima a schermo" non si configura più qui: è sempre
  // disponibile e predefinita nella pagina Etichette (vedi LabelsPage).
  const activePrinters = devicePrinters.filter((p) => p.driver !== 'preview' && isPrinterUsableHere(p.driver));

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Stampante</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding" style={{ '--background': '#eef2f7' } as React.CSSProperties}>

        {/* Stampanti già configurate con stato live */}
        {activePrinters.length > 0 && (
          <IonList inset>
            <IonListHeader>
              <IonLabel>Configurate</IonLabel>
            </IonListHeader>
            {activePrinters.map((p) => {
              const online = isAndroid ? connStatus[p.address] === true : false;
              return (
                <div key={p.id}>
                  <IonItem lines="none">
                    <IonIcon
                      slot="start"
                      icon={online ? checkmarkCircle : alertCircle}
                      color={online ? 'success' : 'medium'}
                    />
                    <IonLabel className="ion-text-wrap">
                      <h3>
                        {p.name}
                        {defaultId === p.id && (
                          <IonBadge color="primary" style={{ fontSize: 10, marginLeft: 8, verticalAlign: 'middle' }}>
                            ⭐ Predefinita
                          </IonBadge>
                        )}
                      </h3>
                      <p style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                        {p.address}
                      </p>
                      {p.driver === 'tspl' && isY428Printer(p.name) && (
                        <p>VEVOR Y428BT · 300 dpi · prova 50 × 30 mm</p>
                      )}
                      {isAndroid && (
                        <IonBadge color={online ? 'success' : 'medium'} style={{ fontSize: 10 }}>
                          {online ? 'Connessa' : 'Disconnessa'}
                        </IonBadge>
                      )}
                    </IonLabel>
                  </IonItem>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                      padding: '0 12px 8px',
                    }}
                  >
                    <IonButton
                      fill="outline" size="small"
                      onClick={() => { void testPrint(p); }}
                      disabled={testing === p.id}
                    >
                      {testing === p.id
                        ? <IonSpinner name="dots" />
                        : <><IonIcon icon={printOutline} slot="start" /> Prova</>}
                    </IonButton>
                    {/* T-281: imposta come predefinita (usata da tutte le sezioni). */}
                    <IonButton
                      fill={defaultId === p.id ? 'solid' : 'outline'}
                      size="small"
                      color="primary"
                      onClick={() => { void makeDefault(p); }}
                      disabled={defaultId === p.id}
                    >
                      <IonIcon icon={defaultId === p.id ? star : starOutline} slot="start" />
                      {defaultId === p.id ? 'Predefinita' : 'Rendi predefinita'}
                    </IonButton>
                    {/* T-272/T-273: Calibra / Test BMP sono comandi TSPL via
                        Bluetooth → non hanno senso per le stampanti di sistema. */}
                    {p.driver !== 'system' && p.driver !== 'qz' && (
                      <>
                        <IonButton
                          fill="outline" size="small" color="warning"
                          onClick={() => { void calibrationPrint(p); }}
                          disabled={testing === p.id}
                        >
                          Calibra
                        </IonButton>
                        <IonButton
                          fill="outline" size="small" color="success"
                          onClick={() => { void bitmapTest(p); }}
                          disabled={testing === p.id}
                        >
                          Test BMP
                        </IonButton>
                      </>
                    )}
                    <IonButton
                      fill="outline" size="small" color="danger"
                      onClick={() => setDeleteTarget(p)}
                    >
                      <IonIcon icon={trashOutline} slot="start" /> Rimuovi
                    </IonButton>
                  </div>
                </div>
              );
            })}
          </IonList>
        )}

        {/* T-424 — Anteprima a schermo (demo): sempre disponibile, anche su PWA
            senza hardware. Non stampa: mostra l'etichetta a schermo. */}
        <IonList inset>
          <IonListHeader>
            <IonLabel>Anteprima / Demo</IonLabel>
          </IonListHeader>
          <IonItem lines="none">
            <IonIcon slot="start" icon={printOutline} color="tertiary" />
            <IonLabel className="ion-text-wrap">
              <h3>
                {PREVIEW_PRINTER_NAME}
                {defaultId === PREVIEW_PRINTER_ID && (
                  <IonBadge color="primary" style={{ fontSize: 10, marginLeft: 8, verticalAlign: 'middle' }}>
                    ⭐ Predefinita
                  </IonBadge>
                )}
              </h3>
              <p style={{ fontSize: 12 }}>
                Mostra l&apos;etichetta a schermo senza stampare. Utile per dimostrazioni e su PWA senza
                stampante fisica.
              </p>
            </IonLabel>
          </IonItem>
          <div style={{ padding: '0 12px 8px' }}>
            <IonButton
              fill={defaultId === PREVIEW_PRINTER_ID ? 'solid' : 'outline'}
              size="small"
              color="primary"
              onClick={() => { void makePreviewDefault(); }}
              disabled={defaultId === PREVIEW_PRINTER_ID}
            >
              <IonIcon icon={defaultId === PREVIEW_PRINTER_ID ? star : starOutline} slot="start" />
              {defaultId === PREVIEW_PRINTER_ID ? 'Predefinita' : 'Usa come predefinita (demo)'}
            </IonButton>
          </div>
        </IonList>

        {/* Aggiungi stampante */}
        <IonList inset>
          <IonListHeader>
            <IonLabel>Aggiungi stampante Bluetooth</IonLabel>
          </IonListHeader>
          <IonItem>
            <IonLabel position="stacked">Larghezza etichetta</IonLabel>
            <IonSelect
              value={paperMm}
              onIonChange={(ev) => setPaperMm(Number(ev.detail.value) || 50)}
            >
              <IonSelectOption value={50}>50×30 mm (Clabel CT221B)</IonSelectOption>
              <IonSelectOption value={60}>60×40 mm</IonSelectOption>
              <IonSelectOption value={80}>80×60 mm</IonSelectOption>
              <IonSelectOption value={100}>100×70 mm</IonSelectOption>
            </IonSelect>
          </IonItem>
          {isAndroid && (
            <IonItem lines="none">
              <IonNote style={{ fontSize: 12 }}>
                Su Android la stampante deve essere prima accoppiata in
                <b> Impostazioni → Bluetooth</b>. TrackEAT poi gestisce la connessione
                in autonomia, senza bisogno di aprire l'app del produttore.
              </IonNote>
            </IonItem>
          )}
          {!isAndroid && (
            <IonItem lines="none">
              <IonNote style={{ fontSize: 12 }}>
                Modalità PWA: il selettore Bluetooth del browser si aprirà al click.
                Per un'esperienza affidabile usa l'APK TrackEAT Android.
              </IonNote>
            </IonItem>
          )}
        </IonList>

        <IonButton
          expand="block"
          onClick={() => { void startScan(); }}
          disabled={scanning}
          style={{ marginBottom: 8 }}
        >
          {scanning
            ? <><IonSpinner name="dots" style={{ marginRight: 8 }} /> Ricerca…</>
            : <><IonIcon icon={refreshOutline} slot="start" /> Cerca stampanti</>}
        </IonButton>

        {/* T-273: Stampanti di sistema via QZ Tray (solo PWA web). Elenca le
            stampanti reali del PC e stampa sulla scelta PER NOME, senza toccare
            la predefinita. Richiede QZ Tray installato sul computer. */}
        {isWeb && (
          <IonList inset>
            <IonListHeader>
              <IonLabel>Stampanti di sistema (QZ Tray)</IonLabel>
            </IonListHeader>
            <IonItem lines="none">
              <IonNote style={{ fontSize: 12 }}>
                Consigliato per stampanti <b>USB</b> (es. Meteor Mirò): vedi l'elenco
                delle stampanti del computer e scegli <b>esattamente</b> quella giusta
                (la predefinita di Windows resta libera per Word/Excel). Richiede
                <b> QZ Tray</b> installato e in esecuzione sul PC; la prima volta clicca
                <b> Allow + Remember</b> nella finestra di QZ Tray.
              </IonNote>
            </IonItem>
            <IonButton
              expand="block"
              onClick={() => { void scanQz(); }}
              disabled={qzScanning}
              style={{ margin: '0 16px 8px' }}
            >
              {qzScanning
                ? <><IonSpinner name="dots" style={{ marginRight: 8 }} /> Connessione a QZ Tray…</>
                : <><IonIcon icon={refreshOutline} slot="start" /> Cerca stampanti (QZ Tray)</>}
            </IonButton>
            {foundQz.length > 0 && (
              <>
                <IonListHeader>
                  <IonLabel>Stampanti trovate</IonLabel>
                </IonListHeader>
                {foundQz.map((name) => (
                  <IonItem key={name} button onClick={() => { void saveQzPrinter(name); }}>
                    <IonIcon slot="start" icon={printOutline} />
                    <IonLabel className="ion-text-wrap"><h3>{name}</h3></IonLabel>
                  </IonItem>
                ))}
              </>
            )}
          </IonList>
        )}

        {/* T-272: Stampante di sistema (solo PWA web). Per stampanti USB
            collegate al PC (es. Meteor Mirò Bluecoop): stampa via driver
            Windows con window.print(), nessun pairing Bluetooth. Alternativa
            senza installare nulla, ma apre la finestra di Chrome. */}
        {isWeb && (
          <IonList inset>
            <IonListHeader>
              <IonLabel>Stampante di sistema (Windows/Chrome)</IonLabel>
            </IonListHeader>
            <IonItem lines="none">
              <IonNote style={{ fontSize: 12 }}>
                Per stampanti collegate via <b>USB</b> al computer (es. Meteor Mirò):
                l'etichetta viene inviata al driver di Windows. Installa prima la
                stampante in Windows. Per stamparla a <b>1 tap senza finestra</b>,
                avvia Chrome con <b>--kiosk-printing</b> e imposta la Meteor come
                stampante predefinita.
              </IonNote>
            </IonItem>
            <IonButton
              expand="block"
              fill="outline"
              onClick={() => { void addSystemPrinter(); }}
              style={{ margin: '0 16px 8px' }}
            >
              <IonIcon icon={printOutline} slot="start" /> Aggiungi stampante di sistema
            </IonButton>
          </IonList>
        )}

        {/* Risultati BT Classic (Android) */}
        {foundClassic.length > 0 && (
          <IonList inset>
            <IonListHeader>
              <IonLabel>Dispositivi accoppiati</IonLabel>
            </IonListHeader>
            {foundClassic.map((dev) => {
              // T-081: badge "Non supportata" sui Niimbot prima del tap.
              const unsupported = isNiimbotDevice(dev.name);
              return (
                <IonItem
                  key={dev.address} button
                  onClick={() => { void savePrinter(dev.name, dev.address, 'classic'); }}
                >
                  <IonLabel>
                    <h3>{dev.name}{unsupported ? ' ⚠️' : ''}</h3>
                    <p style={{ fontFamily: 'monospace', fontSize: 11 }}>{dev.address}</p>
                    {unsupported && (
                      <p style={{ fontSize: 11, color: '#b45309' }}>
                        Niimbot — driver in sviluppo (T-082)
                      </p>
                    )}
                  </IonLabel>
                  {dev.rssi !== null && <IonNote slot="end">{dev.rssi} dBm</IonNote>}
                </IonItem>
              );
            })}
          </IonList>
        )}

        {/* Risultati BLE (PWA) */}
        {foundBle.length > 0 && (
          <IonList inset>
            <IonListHeader>
              <IonLabel>Dispositivi trovati</IonLabel>
            </IonListHeader>
            {foundBle.map((dev) => {
              const unsupported = isNiimbotDevice(dev.name);
              return (
                <IonItem
                  key={dev.deviceId} button
                  onClick={() => { void savePrinter(dev.name, dev.deviceId, 'ble'); }}
                >
                  <IonLabel>
                    <h3>{dev.name}{unsupported ? ' ⚠️' : ''}</h3>
                    <p style={{ fontFamily: 'monospace', fontSize: 11 }}>{dev.deviceId}</p>
                    {unsupported && (
                      <p style={{ fontSize: 11, color: '#b45309' }}>
                        Niimbot — driver in sviluppo (T-082)
                      </p>
                    )}
                  </IonLabel>
                  {dev.rssi !== null && <IonNote slot="end">{dev.rssi} dBm</IonNote>}
                </IonItem>
              );
            })}
          </IonList>
        )}

        {/* Nota: la stampante "Anteprima a schermo" è sempre disponibile e
            predefinita nella pagina Etichette — non serve configurarla qui. */}
        <IonNote
          className="ion-margin-top"
          style={{ display: 'block', fontSize: 12, padding: '4px 4px 12px' }}
        >
          Suggerimento: nella pagina Etichette è sempre disponibile e
          preselezionata la stampante <b>"Anteprima a schermo"</b>, che mostra
          l'etichetta come immagine invece di stamparla — utile per provare il
          flusso senza hardware.
        </IonNote>

        <IonAlert
          isOpen={!!deleteTarget}
          header="Rimuovi stampante"
          message={`Vuoi rimuovere "${deleteTarget?.name ?? ''}"?`}
          buttons={[
            { text: 'Annulla', role: 'cancel', handler: () => setDeleteTarget(null) },
            { text: 'Rimuovi', role: 'destructive', handler: () => { if (deleteTarget) void deletePrinter(deleteTarget); } },
          ]}
          onDidDismiss={() => setDeleteTarget(null)}
        />
        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          // Durata adattiva: messaggi lunghi (trace Niimbot, multilinea) restano
          // 30s per dare tempo di leggere/screenshottare. Messaggi corti 3.5s.
          duration={(toast?.length ?? 0) > 120 ? 30_000 : 3_500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
